import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    CircularProgress,
    Tooltip,
    TextField,
    InputAdornment,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    ExpandMore as ExpandMoreIcon,
    Refresh as RefreshIcon,
    ContentCopy,
    Download as DownloadIconMui,
    History as HistoryIcon,
    Search as SearchIcon,
    Close as ClearIcon,
    ErrorOutline as ErrorOutlineIcon,
    FilterAltOff as FilterOffIcon,
    MergeType as MergeIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import LedgerReportsService from '../../../Services/ledgerReports.service.js';
import DocumentService from '../../../Services/documents.service.js';
import { translateLedgerInvoiceError } from '../../../Utils/translateLedgerInvoiceError.js';
import s from './DocumentGenerationLogsModal.module.scss';

// Skip/failure reason codes emitted by the backend that map to friendly translations.
const REASON_KEYS = {
    alreadygenerated: 'AlreadyGenerated',
    duplicateinrequest: 'DuplicateInRequest',
};

// Operation types whose items produce a downloadable PDF document.
const DOWNLOADABLE_OPERATIONS = new Set(['invoice', 'invoicereceipt', 'receipt']);

// An item that was "skipped" only because the document already existed is, from the
// user's point of view, a success: the document is there. Treat any skip that still
// resolved to a real document as a success so the log reflects reality, not internals.
const deriveItemStatus = (item) => {
    const raw = (item?.status || '').toLowerCase();
    if (raw === 'skipped' && item?.documentId) return 'Succeeded';
    return item?.status || '';
};

// Run-level status shown in the header: only "Failed" when EVERYTHING failed,
// "PartialSuccess" when some (but not all) failed, otherwise "Succeeded".
const deriveRunStatus = (run) => {
    const failed = Number(run?.totalFailed || 0);
    const succeeded = Number(run?.totalSucceeded || 0);
    const skipped = Number(run?.totalSkipped || 0);
    const nonFailed = succeeded + skipped;
    if (failed > 0 && nonFailed === 0) return 'Failed';
    if (failed > 0) return 'PartialSuccess';
    return 'Succeeded';
};

// Display order for run items: failures first, then successes, then skips/anything else.
const STATUS_RANK = {
    failed: 0,
    partialsuccess: 1,
    succeeded: 2,
    completed: 2,
    skipped: 3,
};

const sortItemsByStatus = (items) =>
    [...items].sort((a, b) => {
        const ra = STATUS_RANK[deriveItemStatus(a).toLowerCase()] ?? 4;
        const rb = STATUS_RANK[deriveItemStatus(b).toLowerCase()] ?? 4;
        if (ra !== rb) return ra - rb;
        // Stable within a status group: keep processing order.
        return new Date(a.processedAt || 0) - new Date(b.processedAt || 0);
    });

// Turns a raw item message (skip reason or a possibly stack-trace-laden error) into a clean,
// translated, single-line string for display.
const formatItemMessage = (item, t) => {
    const raw = (item?.errorMessage || '').trim();
    if (!raw) return '';

    // Known skip/duplicate reason codes.
    const reasonKey = REASON_KEYS[raw.toLowerCase()];
    if (reasonKey) return t(reasonKey) || raw;

    // Map known server errors to friendly messages (also matches "reporting month closed", etc.).
    const translated = translateLedgerInvoiceError(raw, t);
    if (translated && translated !== raw) return translated;

    // Unknown error: strip the .NET stack trace / file:line noise and any "XException:" prefix.
    let msg = raw.split(/\s+at\s+/)[0];
    msg = msg.replace(/\s*in\s+\/?\S+:line\s+\d+/gi, '').trim();
    const colonIdx = msg.indexOf('Exception:');
    if (colonIdx !== -1) msg = msg.slice(colonIdx + 'Exception:'.length).trim();
    return msg || raw;
};

const OPERATION_TYPES = [
    'Receipt',
    'InvoiceReceipt',
    'Invoice',
    'Payment',
    'MonthlyCharge',
    'OneTimeCharge',
    'RepairReceipt',
];

// Client-side run status filters, shown as clickable stat cards.
const RUN_STATUS_FILTERS = [
    { value: '', labelKey: 'All', tone: '' },
    { value: 'Succeeded', labelKey: 'Succeeded', tone: s.statSuccess },
    { value: 'PartialSuccess', labelKey: 'PartialSuccess', tone: s.statWarning },
    { value: 'Failed', labelKey: 'Failed', tone: s.statError },
];

const statusChipClass = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'succeeded':
        case 'completed':
            return `${s.chip} ${s.chipSuccess}`;
        case 'failed':
            return `${s.chip} ${s.chipError}`;
        case 'partialsuccess':
            return `${s.chip} ${s.chipWarning}`;
        case 'skipped':
            return `${s.chip} ${s.chipMuted}`;
        default:
            return s.chip;
    }
};

const searchFieldSx = {
    height: '40px',
    padding: '0 12px',
    borderRadius: '10px',
    background: '#f5f6fd',
    width: '100%',
    border: '1px solid #e5e7f6',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    transition: 'border-color .15s ease, box-shadow .15s ease, background .15s ease',
    '&:hover': { borderColor: '#c9cff5' },
    '&.Mui-focused, &:focus-within': {
        borderColor: '#304FFF',
        boxShadow: '0 0 0 3px rgba(48, 79, 255, 0.12)',
        background: '#fff',
    },
};

// Resolve the human name of who created the run, mirroring the CollectionPage "createdBy" column:
// prefer the sub-accountant option label, then a translated role, then the raw id.
const resolveCreatorLabel = (uid, utype, t, subAccountantOptions) => {
    if (!uid && !utype) return '-';
    const found = subAccountantOptions?.find((option) => option.value === uid);
    if (found?.label) return found.label;
    if (utype === 'Client') return t('Client') || 'Client';
    if (utype) return t(utype) || utype;
    return uid || '-';
};

// Local display format: dd/mm/yyyy HH:mm:ss (24h).
const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const pad = (n) => String(n).padStart(2, '0');
    const date = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    return `${date} ${time}`;
};

// Backend document type for a run's operation: invoices/invoice-receipts are Incomes,
// receipts are PaymentAcceptances. Anything else has no directly downloadable document.
const docTypeForOperation = (operationType) => {
    switch ((operationType || '').toLowerCase()) {
        case 'invoice':
        case 'invoicereceipt':
            return 'Income';
        case 'receipt':
            return 'PaymentAcceptance';
        default:
            return null;
    }
};

const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
};

function StatusChip({ status, t }) {
    if (!status) return <span className={s.mutedText}>-</span>;
    return <span className={statusChipClass(status)}>{t(status) || status}</span>;
}

function CountCell({ value, tone }) {
    const n = Number(value || 0);
    return (
        <TableCell className={s.count}>
            <span className={n > 0 ? tone : s.mutedText}>{value ?? 0}</span>
        </TableCell>
    );
}

function RunRow({ run, t, direction, subAccountantOptions, token }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    const [loadingItems, setLoadingItems] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // 'copy' (signed generated document) or 'original' (source file uploaded to the Tax Authority).
    const [variant, setVariant] = useState('copy');

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && items === null) {
            try {
                setLoadingItems(true);
                const data = await LedgerReportsService.getDocumentGenerationRunItems(run.id);
                setItems(sortItemsByStatus(data?.items || []));
            } catch (e) {
                toast.error(t('FailedToLoadLogs') || 'Failed to load logs');
                setItems([]);
            } finally {
                setLoadingItems(false);
            }
        }
    };

    const copyId = (id) => {
        if (!id) return;
        navigator.clipboard?.writeText(id);
        toast.info(t('Copied') || 'Copied');
    };

    const documentType = docTypeForOperation(run.operationType);
    // Documents (invoices/receipts) are stored under the run's case (leading business / issuer),
    // not under the payer clientToPayCaseId.
    const issuerCaseId = run.caseId;

    // Items that actually produced a downloadable document.
    const downloadableItems = (items || []).filter((it) => it.documentId && documentType);

    const isOriginal = variant === 'original';

    // Single item -> use the single-document endpoint (returns a signed blob).
    const downloadOne = async (item) => {
        if (!documentType || !item.documentId) return;
        try {
            const blob = await DocumentService.DownloadDocument(token, {
                CaseId: issuerCaseId || item.clientToPayCaseId,
                Id: item.documentId,
                DocumentType: documentType,
                DownloadOriginal: isOriginal,
            });
            triggerBlobDownload(blob, `${item.reference || item.documentId}_${variant}.pdf`);
        } catch (e) {
            toast.error(t('documentDownloadError') || t('MergeOrPrintFailed') || 'Download failed');
        }
    };

    // Bulk: backend downloads in parallel. merge=false => zip of separate files, true => one merged PDF.
    // DownloadOriginal toggles original vs copy for every file in the batch.
    const downloadBulk = async (merge) => {
        if (downloadableItems.length === 0) {
            toast.info(t('NoDocumentsFound') || 'No documents found');
            return;
        }
        setDownloading(true);
        try {
            const blob = await DocumentService.DownloadDocumentsBulk(token, {
                DocumentType: documentType,
                DownloadOriginal: isOriginal,
                Merge: !!merge,
                Documents: downloadableItems.map((it) => ({
                    Id: it.documentId,
                    CaseId: issuerCaseId || it.clientToPayCaseId,
                })),
            });
            const ext = merge ? 'pdf' : 'zip';
            triggerBlobDownload(blob, `documents_${variant}_${new Date().getTime()}.${ext}`);
            toast.success(t('DocumentsDownloaded') || 'Downloaded');
        } catch (e) {
            toast.error(t('MergeOrPrintFailed') || 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const renderItemMessage = (it) => {
        // A skip that resolved to a real document is a success, so don't
        // surface the "already generated" note as if it were a problem.
        const isSkip = (it.status || '').toLowerCase() === 'skipped';
        if (isSkip && it.documentId) return <span className={s.mutedText}>-</span>;
        const msg = formatItemMessage(it, t);
        if (!msg) return <span className={s.mutedText}>-</span>;
        // Only genuine failures are red; skips/duplicates are neutral.
        const isFailure = (it.status || '').toLowerCase() === 'failed';
        return (
            <Tooltip title={msg}>
                <span className={`${s.ellipsis} ${isFailure ? s.errorText : s.mutedText}`}>{msg}</span>
            </Tooltip>
        );
    };

    const renderItems = () => {
        if (loadingItems) {
            return (
                <div className={s.loading}>
                    <CircularProgress size={22} className={s.spinner} />
                </div>
            );
        }
        if (!items || items.length === 0) {
            return <div className={s.emptyState}>{t('NoItemsInRun') || 'No line items recorded for this run.'}</div>;
        }
        const canDownloadOne = DOWNLOADABLE_OPERATIONS.has((run.operationType || '').toLowerCase());
        return (
            <div className={s.tableWrap} dir={direction} style={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('ClientName') || 'Client'}</TableCell>
                            <TableCell>{t('Status') || 'Status'}</TableCell>
                            <TableCell>{t('Reference') || 'Reference'}</TableCell>
                            <TableCell>{t('Details') || 'Details'}</TableCell>
                            <TableCell className={s.count}>{t('Payments') || 'Payments'}</TableCell>
                            <TableCell>{t('Document') || 'Document'}</TableCell>
                            <TableCell>{t('Error') || 'Error'}</TableCell>
                            <TableCell>{t('ProcessedAt') || 'Processed'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((it) => (
                            <TableRow key={it.id}>
                                <TableCell>
                                    <div className={s.clientCell}>
                                        <span className={s.clientName}>{it.clientName || '-'}</span>
                                        {it.clientToPayCaseId && (
                                            <span className={s.clientCase}>{it.clientToPayCaseId}</span>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell><StatusChip status={deriveItemStatus(it)} t={t} /></TableCell>
                                <TableCell>{it.reference || <span className={s.mutedText}>-</span>}</TableCell>
                                <TableCell>
                                    {it.details ? (
                                        <Tooltip title={it.details}>
                                            <span className={s.ellipsis}>{it.details}</span>
                                        </Tooltip>
                                    ) : (
                                        <span className={s.mutedText}>-</span>
                                    )}
                                </TableCell>
                                <TableCell className={s.count}>
                                    <Tooltip title={it.paymentIdsCsv || ''}>
                                        <span>{it.paymentIdsCsv ? `${it.paymentIdsCsv.split(',').length}` : '-'}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    {it.documentId ? (
                                        <span className={s.docCell}>
                                            <Tooltip title={`${t('CopyId') || 'Copy ID'}: ${it.documentId}`}>
                                                <button
                                                    type="button"
                                                    className={`${s.chip} ${s.chipSuccess}`}
                                                    onClick={() => copyId(it.documentId)}
                                                >
                                                    {t('Created') || 'Created'}
                                                    <ContentCopy className={s.chipIcon} />
                                                </button>
                                            </Tooltip>
                                            {canDownloadOne && (
                                                <Tooltip title={t('Download') || 'Download'}>
                                                    <IconButton
                                                        size="small"
                                                        className={s.iconBtn}
                                                        onClick={() => downloadOne(it)}
                                                        aria-label={t('Download') || 'Download'}
                                                    >
                                                        <DownloadIconMui sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </span>
                                    ) : (
                                        <span className={s.mutedText}>-</span>
                                    )}
                                </TableCell>
                                <TableCell>{renderItemMessage(it)}</TableCell>
                                <TableCell><span className={s.date}>{formatDateTime(it.processedAt)}</span></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        );
    };

    const runStatus = deriveRunStatus(run);
    const operationLabel = t(run.operationType) || run.operationType;

    return (
        <>
            <TableRow
                className={`${s.runRow}${open ? ` ${s.runRowOpen}` : ''}`}
                onClick={toggle}
                aria-expanded={open}
            >
                <TableCell className={s.expandCell}>
                    <IconButton
                        size="small"
                        aria-label={open ? t('Collapse') || 'Collapse' : t('Expand') || 'Expand'}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggle();
                        }}
                    >
                        <ExpandMoreIcon className={`${s.chevron}${open ? ` ${s.chevronOpen}` : ''}`} />
                    </IconButton>
                </TableCell>
                <TableCell><span className={s.clientName}>{operationLabel}</span></TableCell>
                <TableCell><span className={s.date}>{formatDateTime(run.startedAt)}</span></TableCell>
                <TableCell><StatusChip status={runStatus} t={t} /></TableCell>
                <CountCell value={run.totalRequested} tone="" />
                <CountCell value={run.totalSucceeded} tone={s.successText} />
                <CountCell value={run.totalFailed} tone={s.errorText} />
                <CountCell value={run.totalSkipped} tone="" />
                <TableCell>
                    <span className={s.creator}>
                        {resolveCreatorLabel(run.createdByUserId, run.createdByUserType, t, subAccountantOptions)}
                    </span>
                </TableCell>
            </TableRow>
            <TableRow className={open ? undefined : s.detailRowClosed}>
                <TableCell className={s.detailCell} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <div className={s.detail}>
                            {!loadingItems && downloadableItems.length > 0 && (
                                <div className={s.detailBar} dir={direction}>
                                    <span className={s.detailTitle}>
                                        {`${downloadableItems.length} ${t('Document') || 'Document'}`}
                                    </span>
                                    <div className={s.tabs} role="group">
                                        {['copy', 'original'].map((v) => (
                                            <button
                                                key={v}
                                                type="button"
                                                className={`${s.tab} ${s.tabSm}${variant === v ? ` ${s.tabActive}` : ''}`}
                                                aria-pressed={variant === v}
                                                disabled={downloading}
                                                onClick={() => setVariant(v)}
                                            >
                                                {v === 'copy' ? t('Copy') || 'Copy' : t('Original') || 'Original'}
                                            </button>
                                        ))}
                                    </div>
                                    <span className={s.spacer} />
                                    <button
                                        type="button"
                                        className={`${s.secondaryBtn} ${s.smallBtn}`}
                                        disabled={downloading}
                                        onClick={() => downloadBulk(false)}
                                    >
                                        {downloading ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : (
                                            <DownloadIconMui fontSize="small" />
                                        )}
                                        {t('DownloadDocuments') || 'Download'}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${s.primaryBtn} ${s.smallBtn}`}
                                        disabled={downloading}
                                        onClick={() => downloadBulk(true)}
                                    >
                                        {downloading ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : (
                                            <MergeIcon fontSize="small" />
                                        )}
                                        {t('DownloadAsOneFile') || 'Download as one file'}
                                    </button>
                                </div>
                            )}
                            {renderItems()}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function DocumentGenerationLogsModal({ open, onClose, caseId, t, direction = 'ltr', subAccountantOptions = [], token = '' }) {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [operationType, setOperationType] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const isRtl = direction === 'rtl';

    const fetchRuns = useCallback(async () => {
        if (!caseId) return;
        try {
            setLoading(true);
            setLoadError(false);
            const data = await LedgerReportsService.getDocumentGenerationRuns(caseId, {
                operationType: operationType || undefined,
            });
            setRuns(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(t('FailedToLoadLogs') || 'Failed to load logs');
            setLoadError(true);
            setRuns([]);
        } finally {
            setLoading(false);
        }
    }, [caseId, operationType, t]);

    useEffect(() => {
        if (open) fetchRuns();
    }, [open, fetchRuns]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    const statusCounts = useMemo(() => {
        const counts = { '': runs.length, Succeeded: 0, PartialSuccess: 0, Failed: 0 };
        runs.forEach((run) => {
            counts[deriveRunStatus(run)] += 1;
        });
        return counts;
    }, [runs]);

    const visibleRuns = useMemo(() => {
        const q = search.trim().toLowerCase();
        return runs.filter((run) => {
            const status = deriveRunStatus(run);
            if (statusFilter && status !== statusFilter) return false;
            if (!q) return true;
            const haystack = [
                t(run.operationType) || run.operationType,
                t(status) || status,
                formatDateTime(run.startedAt),
                resolveCreatorLabel(run.createdByUserId, run.createdByUserType, t, subAccountantOptions),
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [runs, statusFilter, search, t, subAccountantOptions]);

    const hasClientFilters = !!statusFilter || !!search.trim();
    const clearFilters = () => {
        setStatusFilter('');
        setSearch('');
        setOperationType('');
    };

    if (!open) return null;

    const renderContent = () => {
        if (loading) {
            return (
                <div className={s.loading}>
                    <CircularProgress className={s.spinner} />
                    <span className={s.muted}>{t('Loading') || 'Loading...'}</span>
                </div>
            );
        }
        if (loadError) {
            return (
                <div className={`${s.emptyState} ${s.errorState}`}>
                    <ErrorOutlineIcon className={s.emptyIcon} />
                    {t('FailedToLoadLogs') || 'Failed to load logs'}
                    <button type="button" className={s.secondaryBtn} onClick={fetchRuns}>
                        <RefreshIcon fontSize="small" />
                        {t('TryAgain') || 'Try again'}
                    </button>
                </div>
            );
        }
        if (runs.length === 0) {
            return (
                <div className={s.emptyState}>
                    <HistoryIcon className={s.emptyIcon} />
                    {t('NoGenerationLogs') || 'No generation logs found for this case.'}
                    {operationType && (
                        <button type="button" className={s.ghostBtn} onClick={clearFilters}>
                            <FilterOffIcon fontSize="small" />
                            {t('ClearFilters') || 'Clear filters'}
                        </button>
                    )}
                </div>
            );
        }
        if (visibleRuns.length === 0) {
            return (
                <div className={s.emptyState}>
                    <SearchIcon className={s.emptyIcon} />
                    {t('NoResultsFound') || 'No results found'}
                    <button type="button" className={s.ghostBtn} onClick={clearFilters}>
                        <FilterOffIcon fontSize="small" />
                        {t('ClearFilters') || 'Clear filters'}
                    </button>
                </div>
            );
        }
        return (
            <div className={`${s.tableWrap} ${s.runsWrap}`} dir={direction}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell className={s.expandCell} />
                            <TableCell>{t('OperationType') || 'Operation'}</TableCell>
                            <TableCell>{t('Date') || 'Date'}</TableCell>
                            <TableCell>{t('Status') || 'Status'}</TableCell>
                            <TableCell className={s.count}>{t('Requested') || 'Requested'}</TableCell>
                            <TableCell className={s.count}>{t('Succeeded') || 'Succeeded'}</TableCell>
                            <TableCell className={s.count}>{t('Failed') || 'Failed'}</TableCell>
                            <TableCell className={s.count}>{t('Skipped') || 'Skipped'}</TableCell>
                            <TableCell>{t('CreatedBy') || 'By'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRuns.map((run) => (
                            <RunRow
                                key={run.id}
                                run={run}
                                t={t}
                                direction={direction}
                                subAccountantOptions={subAccountantOptions}
                                token={token}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>
        );
    };

    return (
        <Portal>
            <div className={`${s.overlay} ${isRtl ? s.rtl : s.ltr}`} onClick={onClose}>
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={s.modal}>
                        <div className={s.body}>
                            <div className={s.header}>
                                <div className={s.headerStart}>
                                    <Tooltip title={t('Close') || 'Close'}>
                                        <IconButton onClick={onClose} className={s.closeIcon} aria-label={t('Close') || 'Close'}>
                                            <CancelIcon className={s.closeIcon} />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                                <div className={s.headerTitle}>
                                    <HistoryIcon />
                                    {t('GenerationLogs') || 'Generation Logs'}
                                </div>
                                <div className={s.headerEnd}>
                                    <button
                                        type="button"
                                        className={`${s.ghostBtn} ${s.smallBtn}`}
                                        onClick={fetchRuns}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                                        <span className={s.refreshLabel}>{t('Refresh') || 'Refresh'}</span>
                                    </button>
                                </div>
                            </div>

                            <div className={s.stats}>
                                {RUN_STATUS_FILTERS.map((f) => (
                                    <button
                                        key={f.value || 'all'}
                                        type="button"
                                        className={`${s.statCard} ${f.tone}${statusFilter === f.value ? ` ${s.statCardActive}` : ''}`}
                                        aria-pressed={statusFilter === f.value}
                                        onClick={() => setStatusFilter(statusFilter === f.value ? '' : f.value)}
                                    >
                                        <span className={s.statLabel}>
                                            {f.value ? t(f.labelKey) : t('GenerationRuns')}
                                        </span>
                                        <span className={s.statValue}>{loading ? '–' : statusCounts[f.value]}</span>
                                    </button>
                                ))}
                            </div>

                            <div className={s.toolbar}>
                                <div className={s.toolbarRow}>
                                    <div className={s.searchField}>
                                        <TextField
                                            variant="standard"
                                            fullWidth
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder={t('Search') || 'Search'}
                                            InputProps={{
                                                disableUnderline: true,
                                                sx: searchFieldSx,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" sx={{ color: '#304FFF' }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: search ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => setSearch('')} aria-label={t('Clear') || 'Clear'}>
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                        />
                                    </div>
                                    {(hasClientFilters || operationType) && (
                                        <button type="button" className={`${s.ghostBtn} ${s.smallBtn}`} onClick={clearFilters}>
                                            <FilterOffIcon fontSize="small" />
                                            {t('ClearFilters') || 'Clear filters'}
                                        </button>
                                    )}
                                </div>
                                <div className={s.toolbarRow}>
                                    <span className={s.fieldLabel}>{t('OperationType') || 'Operation'}</span>
                                    <div className={s.tabs} role="group">
                                        {['', ...OPERATION_TYPES].map((op) => (
                                            <button
                                                key={op || 'all'}
                                                type="button"
                                                className={`${s.tab}${operationType === op ? ` ${s.tabActive}` : ''}`}
                                                aria-pressed={operationType === op}
                                                onClick={() => setOperationType(op)}
                                            >
                                                {op ? t(op) || op : t('All') || 'All'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className={s.content}>{renderContent()}</div>

                            <div className={s.footer}>
                                {!loading && runs.length > 0 && (
                                    <span className={s.footerInfo}>
                                        {`${t('GenerationRuns')}: ${visibleRuns.length}${visibleRuns.length !== runs.length ? ` / ${runs.length}` : ''}`}
                                    </span>
                                )}
                                <span className={s.spacer} />
                                <button type="button" className={s.secondaryBtn} onClick={onClose}>
                                    {t('Close') || 'Close'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
}
